import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

const TransportReferenceLegApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    transportModeReference: z.object({
        id: z.number(),
        code: z.string(),
        isRefrigerated: z.boolean().optional(),
    }),
    name: z.string().nullable().optional(),
    distanceKm: z.number().nullable().optional(),
    isRefrigerated: z.boolean(),
    originCountryCode: z.string().nullable().optional(),
    destinationCountryCode: z.string().nullable().optional(),
});

export type TransportReferenceLeg = z.infer<typeof TransportReferenceLegApiSchema>;

type SearchOptions = {
    originCountryCode?: string;
    destinationCountryCode?: string;
    nameQuery?: string;
    enabled?: boolean;
};

export const useSearchTransportReferenceLegs = ({
    originCountryCode,
    destinationCountryCode,
    nameQuery,
    enabled = true,
}: SearchOptions = {}) => {
    const { i18n } = useTranslation();

    const query = useQuery({
        enabled,
        cacheTime: 0,
        staleTime: 0,
        queryKey: [
            "searchTransportReferenceLegs",
            originCountryCode ?? null,
            destinationCountryCode ?? null,
            nameQuery ?? null,
            i18n.language,
        ],
        queryFn: async () => {
            return await sureApiCall.get("/api/transport_leg_references", {
                params: {
                    itemsPerPage: 50,
                    ...(originCountryCode ? { originCountryCode } : {}),
                    ...(destinationCountryCode ? { destinationCountryCode } : {}),
                    ...(nameQuery ? { name: nameQuery } : {}),
                },
            });
        },
        select: (request) => {
            const members = request.data["hydra:member"] ?? request.data.member ?? [];
            return zodParseWithLog(members, TransportReferenceLegApiSchema.array(), "searchTransportReferenceLegs");
        },
    });

    return {
        transportReferenceLegs: query.data ?? [],
        ...query,
    };
};
